import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import { Moon, Calendar, Heart, BookOpen } from 'lucide-react';
import { LunarPhaseData, calculateCycleDay, getCyclePhase } from '@/utils/lunarPhases';
import { supabase } from '@/integrations/supabase/client';

interface DateDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: Date;
  cycleStartDate: Date;
  cycleLength: number;
  lunarPhase: LunarPhaseData;
  isPerimenopausal?: boolean;
  isMenopausal?: boolean;
}

export default function DateDetailsDialog({ 
  open, 
  onOpenChange, 
  date, 
  cycleStartDate, 
  cycleLength, 
  lunarPhase,
  isPerimenopausal = false,
  isMenopausal = false
}: DateDetailsDialogProps) {
  const [dailyData, setDailyData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  const cycleDay = calculateCycleDay(cycleStartDate, date);
  const cyclePhase = getCyclePhase(cycleDay, cycleLength, isPerimenopausal, isMenopausal);

  // Fetch daily check-in data for this date
  useEffect(() => {
    if (open && date) {
      fetchDailyData();
    }
  }, [open, date]);

  const fetchDailyData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('date', format(date, 'yyyy-MM-dd'))
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching daily data:', error);
      } else {
        setDailyData(data);
      }
    } catch (error) {
      console.error('Error fetching daily data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {format(date, 'EEEE, MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Moon className="h-5 w-5" />
              Lunar Phase Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-medium">{lunarPhase.name}</div>
              <div className="text-sm text-muted-foreground">
                Illumination: {lunarPhase.illumination}%
              </div>
              <div className="text-sm text-muted-foreground">
                {lunarPhase.cycleDescription}
              </div>
              <div className="text-sm font-medium text-primary">
                {lunarPhase.waxingDescription}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              {isMenopausal ? 'Menopause Status' : isPerimenopausal ? 'Perimenopause Status' : 'Menstrual Cycle Details'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {!isMenopausal && !isPerimenopausal && (
                  <span className="font-medium">Day {cycleDay}</span>
                )}
                <span className={`font-medium ${cyclePhase.color}`}>
                  {cyclePhase.name}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                {cyclePhase.description}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily tracking data if available */}
        {dailyData && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Daily Tracking Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {dailyData.menstrual_flow && (
                <div>
                  <span className="font-medium">Flow: </span>
                  <span className="text-sm">{dailyData.menstrual_flow}</span>
                </div>
              )}
              {dailyData.menstrual_symptoms && dailyData.menstrual_symptoms.length > 0 && (
                <div>
                  <span className="font-medium">Symptoms: </span>
                  <span className="text-sm">{dailyData.menstrual_symptoms.join(', ')}</span>
                </div>
              )}
              {dailyData.mood_physical_impact && (
                <div>
                  <span className="font-medium">Mood/Physical Impact: </span>
                  <span className="text-sm">{dailyData.mood_physical_impact}</span>
                </div>
              )}
              {dailyData.thought_impact && (
                <div>
                  <span className="font-medium">Thought Impact: </span>
                  <span className="text-sm">{dailyData.thought_impact}</span>
                </div>
              )}
              {dailyData.relationship_impact && (
                <div>
                  <span className="font-medium">Relationship Impact: </span>
                  <span className="text-sm">{dailyData.relationship_impact}</span>
                </div>
              )}
              {dailyData.cycle_insights && (
                <div>
                  <span className="font-medium">Cycle Insights: </span>
                  <span className="text-sm">{dailyData.cycle_insights}</span>
                </div>
              )}
              {dailyData.menopause_symptoms && dailyData.menopause_symptoms.length > 0 && (
                <div>
                  <span className="font-medium">Menopause Symptoms: </span>
                  <span className="text-sm">{dailyData.menopause_symptoms.join(', ')}</span>
                </div>
              )}
              {dailyData.hot_flashes_frequency > 0 && (
                <div>
                  <span className="font-medium">Hot Flashes: </span>
                  <span className="text-sm">{dailyData.hot_flashes_frequency} times</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Optional advice based on cycle phase */}
        {cyclePhase.phase === 'menstruation' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Rest & Restore</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                This is a time for rest and reflection. Focus on gentle movement, 
                warm foods, and self-care practices.
              </p>
            </CardContent>
          </Card>
        )}
        
        {cyclePhase.phase === 'follicular' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">New Beginnings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Your energy is building. This is a great time to start new projects 
                and engage in creative activities.
              </p>
            </CardContent>
          </Card>
        )}
        
        {cyclePhase.phase === 'ovulation' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Peak Energy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                You're at your most fertile and energetic. Great time for social 
                activities and challenging workouts.
              </p>
            </CardContent>
          </Card>
        )}
        
        {cyclePhase.phase === 'luteal' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Harvest & Preparation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Time to complete projects and prepare for your next cycle. 
                Focus on organization and self-care.
              </p>
            </CardContent>
          </Card>
        )}

        {cyclePhase.phase === 'perimenopause' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Transition & Wisdom</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Your body is transitioning. Focus on stress management, bone health, 
                and honouring your body's changing needs.
              </p>
            </CardContent>
          </Card>
        )}

        {cyclePhase.phase === 'menopause' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Wisdom & Freedom</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                You've entered your wisdom years. Focus on maintaining vitality, 
                bone health, and embracing this new phase of life.
              </p>
            </CardContent>
          </Card>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}